import {useCallback, useEffect, useRef, type MutableRefObject} from 'react';
import {computeResizedWidth, WIDGET_NATURAL_WIDTH} from 'app/components/pages/poker/proom/proomWidgetGeometry';

interface ResizeHandlers {
    blur: (() => void) | null
    end: ((e: PointerEvent) => void) | null
    lostCapture: (() => void) | null
    move: ((e: PointerEvent) => void) | null
}

interface UseProomTableWindowResizeParams {
    boardId: string
    isMobile: boolean
    onSizeChange: (boardId: string, width: number) => void
    width: number
}

interface ResizeRefs {
    handlers: MutableRefObject<ResizeHandlers>
    handleElement: MutableRefObject<HTMLDivElement | null>
    start: MutableRefObject<{
        pointerId: number
        pointerX: number
        pointerY: number
        width: number
    }>
}

interface AttachResizeListenersParams {
    boardId: string
    cleanupResize: () => void
    handleElement: HTMLDivElement
    onSizeChange: (boardId: string, width: number) => void
    refs: ResizeRefs
}

const POINTER_CAPTURE_OPTIONS: AddEventListenerOptions = {capture: true};

const detachResizeListeners = (refs: ResizeRefs): void => {
    if (refs.handleElement.current && refs.handlers.current.lostCapture) {
        refs.handleElement.current.removeEventListener(
            'lostpointercapture',
            refs.handlers.current.lostCapture
        );
        refs.handlers.current.lostCapture = null;
    }
    refs.handleElement.current = null;

    if (refs.handlers.current.move) {
        window.removeEventListener('pointermove', refs.handlers.current.move, POINTER_CAPTURE_OPTIONS);
        refs.handlers.current.move = null;
    }
    if (refs.handlers.current.end) {
        window.removeEventListener('pointerup', refs.handlers.current.end, POINTER_CAPTURE_OPTIONS);
        window.removeEventListener('pointercancel', refs.handlers.current.end, POINTER_CAPTURE_OPTIONS);
        refs.handlers.current.end = null;
    }
    if (refs.handlers.current.blur) {
        window.removeEventListener('blur', refs.handlers.current.blur);
        refs.handlers.current.blur = null;
    }
};

const attachResizeListeners = ({
    boardId,
    cleanupResize,
    handleElement,
    onSizeChange,
    refs
}: AttachResizeListenersParams): void => {
    const moveHandler = (ev: PointerEvent): void => {
        if (refs.start.current.pointerId !== ev.pointerId) {
            return;
        }

        const newWidth = computeResizedWidth(refs.start.current, {
            x: ev.clientX,
            y: ev.clientY
        });
        onSizeChange(boardId, newWidth);
    };

    const endHandler = (ev: PointerEvent): void => {
        if (refs.start.current.pointerId !== ev.pointerId) {
            return;
        }
        if (handleElement.hasPointerCapture(ev.pointerId)) {
            handleElement.releasePointerCapture(ev.pointerId);
        }
        cleanupResize();
    };

    refs.handlers.current.move = moveHandler;
    refs.handlers.current.end = endHandler;
    refs.handlers.current.blur = cleanupResize;
    refs.handlers.current.lostCapture = cleanupResize;
    handleElement.addEventListener('lostpointercapture', cleanupResize);
    window.addEventListener('pointermove', moveHandler, POINTER_CAPTURE_OPTIONS);
    window.addEventListener('pointerup', endHandler, POINTER_CAPTURE_OPTIONS);
    window.addEventListener('pointercancel', endHandler, POINTER_CAPTURE_OPTIONS);
    window.addEventListener('blur', cleanupResize);
};

export const useProomTableWindowResize = ({
    boardId,
    isMobile,
    onSizeChange,
    width
}: UseProomTableWindowResizeParams): ((event: React.PointerEvent<HTMLDivElement>) => void) => {
    const resizeRefs: ResizeRefs = {
        handleElement: useRef<HTMLDivElement | null>(null),
        handlers: useRef<ResizeHandlers>({blur: null, end: null, lostCapture: null, move: null}),
        start: useRef({
            pointerId: -1,
            pointerX: 0,
            pointerY: 0,
            width: WIDGET_NATURAL_WIDTH
        })
    };

    const cleanupResize = useCallback(() => {
        resizeRefs.start.current.pointerId = -1;
        document.body.style.removeProperty('cursor');
        document.body.style.removeProperty('user-select');
        detachResizeListeners(resizeRefs);
    }, []);

    const handleResizePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (isMobile || event.button !== 0) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        cleanupResize();

        const handleElement = event.currentTarget;
        resizeRefs.handleElement.current = handleElement;
        resizeRefs.start.current = {
            pointerId: event.pointerId,
            pointerX: event.clientX,
            pointerY: event.clientY,
            width
        };

        handleElement.setPointerCapture(event.pointerId);
        document.body.style.cursor = 'nwse-resize';
        document.body.style.userSelect = 'none';
        attachResizeListeners({
            boardId,
            cleanupResize,
            handleElement,
            onSizeChange,
            refs: resizeRefs
        });
    }, [
        boardId,
        cleanupResize,
        isMobile,
        onSizeChange,
        width
    ]);

    useEffect(() => () => cleanupResize(), [cleanupResize]);

    return handleResizePointerDown;
};
